import { Dispatch, ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import * as fabric from "fabric";
import { editorInterface } from "store/slice/editor";
import { CustomTextbox, TextboxState } from "../../@types/textbox";
import store from "../../store";
import { detectOS } from "../utils";
import {
  calculateNewScale,
  createFabricTextbox,
  decreaseFontSize,
  increaseFontSize,
  isOverSize,
  loadFont,
  MAXFONTSIZE,
  MINFONTSIZE,
  PLACEHOLDER_TEXT
} from "./textboxUtils";
import { textAlign } from "html2canvas/dist/types/css/property-descriptors/text-align";


/**
 * Determines and defines the maximum allowable font size for a given textbox
 * without it exceeding specified size constraints.
 *
 * @param {CustomTextbox} textbox - The textbox instance for which the maximum font size is to be calculated.
 * @param {Function} dispatch - The Redux dispatch function used to update the state with the calculated maximum font
 *   size.
 */
export const defineTooBigFontSize = ( textbox: CustomTextbox, dispatch: Function ) => {
  const currentFontSize = Number( textbox.fontSize );
  const getMaxSizes = () => store.getState().editor.maxSizes; // Obtenir maxSizes depuis Redux
  const getScale = () => store.getState().editor.scale;
  const maxSizes = getMaxSizes();
  const scale = getScale();
  const maxFontSize = MAXFONTSIZE * scale || MAXFONTSIZE;

  let newLimitFontSize = maxFontSize;

  for ( let testFontSize = currentFontSize; testFontSize <= maxFontSize; testFontSize++ ) {
    textbox.set( { fontSize: testFontSize } );
    textbox.setCoords();

    if ( isOverSize( textbox, maxSizes ) ) {
      newLimitFontSize = testFontSize - 1;
      break;
    }
  }

  textbox.set( { fontSize: currentFontSize } );
  textbox.setCoords();

  dispatch( {
    type: "editor/updateTooBigFontSizes",
    payload: newLimitFontSize
  } );
};

/**
 * Handles the auto-resizing of a given fabric.Textbox instance based on specified constraints and scaling factors.
 * Adjusts the font size of the textbox to fit within the maximum allowable dimensions, considering optional QR code
 * space.
 *
 * @param {fabric.Textbox} textbox - The fabric.Textbox instance to be resized.
 * @param {boolean} qrcode=false
 */
export const handleAutoResize = ( textbox: fabric.Textbox, qrcode = false, scale = 1 ) => {

  const maxSizes = { ...store.getState().editor.maxSizes };

  let responseBlockHeight = 150 * scale;

  if ( qrcode && responseBlockHeight ) {
    maxSizes.height = Math.max( 0, maxSizes.height - responseBlockHeight );
  }

  let resized = false;

  resized = decreaseFontSize(textbox, maxSizes, scale);
  if (!resized) {
    increaseFontSize(textbox, maxSizes, scale);
  }

  if ( textbox?.canvas ) {
    textbox.canvas.renderAll();
  }
};

/**
 * Generates a new alert rectangle overlay based on the provided custom textbox's dimensions and position.
 *
 * The generated rectangle will have the same dimensions and position as the specified textbox,
 * with customizable properties such as stroke color, stroke width, and fill color. This alert rectangle
 * is designed to visually highlight an area on the canvas where the corresponding textbox resides.
 *
 * @param {CustomTextbox} textbox - The custom textbox object containing the reference dimensions, position, and other
 *   properties.
 * @returns {fabric.Rect} A Fabric.js rectangle object configured to match the textbox's position and dimensions,
 *   styled as an alert overlay.
 */
export const createAlertRect = ( textbox: CustomTextbox ): fabric.Rect => {
  return new fabric.Rect( {
    left: textbox.left,
    top: textbox.top,
    width: textbox.width,
    height: textbox.height,
    originX: textbox.originX,
    originY: textbox.originY,
    absolutePositioned: true,
    stroke: process.env.ALERT_COLOR || "#FF0000",
    strokeWidth: Number( process.env.STROKE_WIDTH_LIGHT ) || 2,
    fill: "rgba(255, 0, 0, 0.1)",
    selectable: false,
    evented: false,
    idx: textbox.idx
  } );
};


/**
 * Handles the selection of a textbox in the editor, ensuring proper initialization
 * and updating the maximum allowed sizes and font scale for the selected textbox.
 *
 * @param {CustomTextbox} textbox - The textbox instance being interacted with.
 * @param {TextboxState} initialState - The initial state of the textbox.
 * @param {Function} dispatch - The dispatch function to update the editor state.
 * @param {number} scale - The scaling factor applied to the editor canvas.
 * @param {Object} itemText - The DOM representation or configuration of the textbox's text.
 */
const handleTextboxSelection = (
  textbox: CustomTextbox,
  initialState: TextboxState,
  dispatch: Function,
  scale: number,
  itemText: any
) => {
  if ( initialState.text === PLACEHOLDER_TEXT ) {
    textbox.set( {
      text: "",
      fill: "#000000"
    } );
  }

  const maxWidth = parseFloat( itemText.style.width ) * scale;
  const maxHeight = parseFloat( itemText.style.height ) * scale;
  const originalFontSize = textbox.fontSize * scale;
  dispatch( {
    type: "editor/updateActualMaxSizes",
    payload: { width: maxWidth, height: maxHeight, fontSize: originalFontSize }
  } );

  defineTooBigFontSize( textbox, dispatch );
};


/**
 * Updates the state of a textbox if any property has changed compared to its initial state.
 *
 * This function compares the current properties of the textbox to its initial state.
 * If any properties have changed, it dispatches an update to the state with the updated fabric canvas data
 * and synchronizes the provided `initialState` object with the current textbox properties.
 *
 * @param {CustomTextbox} textbox - The textbox element to check for state changes.
 * @param {TextboxState} initialState - The initial state of the textbox to compare against.
 * @param {Function} dispatch - The dispatch function to update the state.
 * @param {string} selectedLayout - The ID of the currently selected layout.
 * @param {any} fabricCanvasInstanceRef - A ref object for the current fabric canvas instance.
 * @returns {boolean} - Returns `true` if the textbox state has changed; otherwise, `false`.
 */
const updateTextboxStateIfChanged = (
  textbox: CustomTextbox,
  initialState: TextboxState,
  dispatch: Function,
  selectedLayout: string,
  fabricCanvasInstanceRef: any,
  newScale: number 
): boolean => {
  const scale = store.getState().editor.scale;
  const currentState = {
    text: textbox.text,
    left: textbox.left,
    top: textbox.top,
    angle: textbox.angle,
    width: textbox.width,
    height: textbox.height,
    fontSize: textbox.fontSize,
    fontFamily: textbox.fontFamily,
    textAlign: textbox.textAlign,
    originY: textbox.originY,
    fill: textbox.fill
  };

  const hasChanged = Object.keys(initialState).some(
    (key) => initialState[key as keyof TextboxState] !== currentState[key as keyof TextboxState]
  );

  if (hasChanged) {
    dispatch({
      type: "editor/updateCurrentDataFabricArray",
      payload: {
        layoutId: selectedLayout,
        fabricElements: JSON.stringify(fabricCanvasInstanceRef.current.toJSON())
      }
    });
    dispatch({
      type: "editor/updateTextsData",
      payload: {
        index: textbox.idx,
        layout: selectedLayout,
        value: textbox.text,
        properties: {
          textVAlign: textbox.originY,
          textAlign: textbox.textAlign,
          textFont: textbox.fontFamily,
          textFontFamily: textbox.fontFamily,
          textFontColor: textbox.fill,
          textFontSize: textbox.fontSize / scale / newScale,
          textFontColorType: "color",
        }
      }
    });

    Object.assign(initialState, currentState);
  }

  return hasChanged;
};

/**
 * Handles the deselection of a textbox within a fabric canvas.
 *
 * This function is responsible for ensuring that the state of the textbox is
 * updated if there are any changes. Additionally, if the textbox is empty
 * and has a placeholder property, it sets the placeholder text along with
 * a semi-transparent text fill color.
 *
 * @param {CustomTextbox} textbox - The textbox object to handle deselection for.
 * @param {TextboxState} initialState - The initial state of the textbox for comparison.
 * @param {Function} dispatch - The dispatch function to update the global state.
 * @param {string} selectedLayout - The currently selected layout for the textbox.
 * @param {any} fabricCanvasInstanceRef - The reference to the fabric.js canvas instance.
 */
const handleTextboxDeselection = (
  textbox: CustomTextbox,
  initialState: TextboxState,
  dispatch: Function,
  selectedLayout: string,
  fabricCanvasInstanceRef: any,
  newScale: number
) => {
  updateTextboxStateIfChanged(textbox, initialState, dispatch, selectedLayout, fabricCanvasInstanceRef, newScale);

  if (textbox.text === "" && textbox.placeholder) {
    textbox.set({
      text: PLACEHOLDER_TEXT,
      fill: "rgba(0, 0, 0, 0.5)"
    });
  }
};

/**
 * Handles the autosave logic for a given textbox by updating its state
 * if any changes are detected. This function ensures that the current
 * state of the textbox remains synchronized with the provided initial state.
 *
 * @param {CustomTextbox} textbox - The textbox whose state needs to be managed and autosaved.
 * @param {TextboxState} initialState - The initial state of the textbox to compare against.
 * @param {Function} dispatch - A function to dispatch actions to update the application's state.
 * @param {string} selectedLayout - The currently selected layout in the application.
 * @param {any} fabricCanvasInstanceRef - A reference to the Fabric.js canvas instance associated with the textbox.
 */
const handleTextboxAutosave = (
  textbox: CustomTextbox,
  initialState: TextboxState,
  dispatch: Function,
  selectedLayout: string,
  fabricCanvasInstanceRef: any,
  newScale: number
) => {
  updateTextboxStateIfChanged(textbox, initialState, dispatch, selectedLayout, fabricCanvasInstanceRef, newScale);
};


/**
 * Handles the display of alert messages related to textboxes in a fabric canvas instance when text bounding
 * constraints are exceeded.
 *
 * @param {Object} fabricCanvasInstanceRef - A reference to the current fabric canvas instance.
 * @param {Object} textbox - The textbox object being checked for constraints.
 * @param {number} index - The index of the current textbox in the layout or list.
 * @param {Object} itemText - The DOM element or object containing style information such as height.
 * @param {number} scale - The scaling factor applied to the canvas or elements.
 * @param {Object} selectedLayout - The currently selected layout configuration.
 * @param {Function} dispatch - The dispatch function to trigger state updates.
 * @param {KeyboardEvent} [e] - Optional keyboard event, used to determine if a deletion key is pressed.
 */
const handleAlertMessage = (fabricCanvasInstanceRef, textbox, index, itemText, scale, selectedLayout, dispatch, e?: KeyboardEvent ) => {
  const getMaxSizes = () => store.getState().editor.maxSizes;
  const maxSizes = getMaxSizes();

  const isDeletionKey = e ? e.key === "Backspace" || e.key === "Delete" : false;
  const exceedsBounds = isOverSize(textbox, maxSizes);
  const fontTooSmall = textbox.fontSize <= (MINFONTSIZE * scale);

  if (!isDeletionKey && exceedsBounds && fontTooSmall) {
    e?.preventDefault();

    if (!textbox.alertRect) {
      textbox.alertRect = createAlertRect(textbox);
      fabricCanvasInstanceRef.current.add(textbox.alertRect);
    }

    dispatch( {
      type: "editor/toggleAlert",
      payload: {
        show: true,
        message: "Le texte dépasse de la zone de texte, veuillez réduire la taille de la police ou supprimer du texte.",
        type: "danger",
        index: index,
        layout: selectedLayout
      }
    } );
  } else {
    if ( textbox.alertRect ) {
      textbox.set( { width: maxSizes.width - 1 } );
      fabricCanvasInstanceRef.current.remove( textbox.alertRect );
      fabricCanvasInstanceRef.current.renderAll();
      textbox.alertRect = null;
    }

    dispatch( {
      type: "editor/toggleAlert",
      payload: {
        show: false,
        message: "",
        type: "",
        index: index,
        layout: selectedLayout
      }
    } );
  }
};

/**
 * Configures a fabric.js textbox instance with specified event listeners and behavior.
 *
 * @param {fabric.Textbox} textbox - The text box object to configure.
 * @param {Object} itemText - An object representing text-related data, including style dimensions and other properties.
 * @param {number} scale - The scale factor to normalize text box sizing and positioning.
 * @param {Object} fabricCanvasInstanceRef - The React reference to the fabric.js canvas instance.
 * @param {Function} dispatch - The Redux or context dispatch function to trigger state updates.
 * @param {Object} selectedLayout - The currently selected layout within the canvas editor interface.
 * @param {number} index - The index representing the text box's position in the layout.
 * @param {boolean} [qrcode=false] - A flag for handling QR code-related behavior in text box configuration.
 * @returns {fabric.Textbox} Returns the configured text box instance.
 */
export const configureTextBox = ( textbox,
  itemText,
  scale,
  fabricCanvasInstanceRef,
  dispatch,
  selectedLayout,
  index,
  qrcode = false,
  newScale = 1
 ) => {

  let initialState: TextboxState = {
    text: "",
    left: 0,
    top: 0,
    angle: 0,
    width: 0,
    height: 0,
    fontSize: 0,
    fontFamily: "",
    textAlign: "",
    originY: "",
    fill: ""
  };

  handleTextboxAutosave(
    textbox,
    initialState, // Passez l'état initial
    dispatch,
    selectedLayout,
    fabricCanvasInstanceRef,
    newScale
  );

  const checkAutosave = () => {
    handleTextboxAutosave(
      textbox,
      initialState, // Passez l'état initial
      dispatch,
      selectedLayout,
      fabricCanvasInstanceRef,
      newScale
    );
  };
  let checkInterval;

  let isFirstClick = true;
  let lastActiveObject = null;

  fabricCanvasInstanceRef.current.on("mouse:up", (e) => {
    const activeObject = fabricCanvasInstanceRef.current.getActiveObject();
    const clickedObject = e.target;

    if (clickedObject && clickedObject !== activeObject && clickedObject.type === "textbox") {
      fabricCanvasInstanceRef.current.discardActiveObject();
      fabricCanvasInstanceRef.current.setActiveObject(clickedObject);
      fabricCanvasInstanceRef.current.requestRenderAll();

      isFirstClick = true;
      lastActiveObject = clickedObject;
    }
  });

  textbox.on( "selected", () => {
    checkInterval = setInterval(checkAutosave, 500);
    const activeObject = fabricCanvasInstanceRef.current.getActiveObject();

    if (lastActiveObject !== activeObject) {
      isFirstClick = true;
      lastActiveObject = activeObject;
    }

    initialState = {
      text: textbox.text,
      left: textbox.left,
      top: textbox.top,
      angle: textbox.angle,
      width: textbox.width,
      height: textbox.height,
      fontSize: textbox.fontSize,
      fontFamily: textbox.fontFamily,
      textAlign: textbox.textAlign,
      originY: textbox.originY,
      fill: textbox.fill
    };

    handleTextboxSelection( textbox, initialState, dispatch, scale, itemText );

    const os = detectOS();

    if ( os !== "iOS" ) {
      setTimeout( () => {
        textbox.enterEditing();
      }, 100 )
    } else {
      textbox.enterEditing();
    }

  } );


  textbox.on("mousedown", () => {
    if (isFirstClick) {
      const textLength = textbox.text.length;
      textbox.setSelectionStart(textLength);
      textbox.setSelectionEnd(textLength);
      isFirstClick =false
    }
  });

  textbox.on("editing:entered", () => {
    const handleKeydown = (e) => handleAlertMessage(fabricCanvasInstanceRef, textbox, index, itemText, scale, selectedLayout, dispatch, e);

    document.addEventListener("keydown", handleKeydown);

    const removeListener = () => {
      document.removeEventListener("keydown", handleKeydown);
      textbox.off("editing:exited", removeListener);
    };

    textbox.on("editing:exited", removeListener);
    });

  textbox.on( "changed", () => {

    if ( !textbox.placeholder ) {
      textbox.set( {
        placeholder: true
      } );
    }

    handleAutoResize( textbox, qrcode, scale );
    defineTooBigFontSize( textbox, dispatch );

  } );

  textbox.on( "deselected", () => {
    isFirstClick = true;
    lastActiveObject = null;
    handleTextboxDeselection( textbox, initialState, dispatch, selectedLayout, fabricCanvasInstanceRef, newScale );
    clearInterval(checkInterval);
  } );


  fabricCanvasInstanceRef.current.add( textbox );
  return textbox;
};

/**
 * Creates or configures an existing text box on a Fabric.js canvas.
 *
 * @param {Object} itemText - The text item information including styles and dimensions.
 * @param {number | undefined} [scale=1] - Scale factor to adjust positions and dimensions.
 * @param {fabric.Canvas} fabricCanvasInstanceRef - The reference to the Fabric.js canvas instance.
 * @param {ThunkDispatch<{}, undefined, UnknownAction> & Dispatch<UnknownAction>} dispatch - The Redux dispatch
 *   function for managing application state.
 * @param {number} selectedLayout - The identifier for the currently selected layout.
 * @param {Object} fabricElement - Properties of the Fabric.js text element (e.g., width, position, font).
 * @param {number} fabricElement.width - The width of the text element.
 * @param {string} fabricElement.text - The text content to display.
 * @param {number} fabricElement.left - The left position of the text element.
 * @param {number} fabricElement.top - The top position of the text element.
 * @param {number} fabricElement.fontSize - The font size of the text element.
 * @param {any} fabricElement.textAlign - The text alignment property.
 * @param {any} fabricElement.fill - The text color or fill style.
 * @param {any} fabricElement.fontFamily - The font family of the text element.
 * @param {any} fabricElement.originY - The origin Y property of the text element.
 * @param {number} index - The index of the text box being processed.
 * @param {boolean} [qrcode=false] - Whether the text box is associated with a QR code.
 * @returns {fabric.Textbox} - The created or configured Fabric.js text box instance.
 */
export const createCanvasTextBoxExisting = async(
  itemText,
  scale: number | undefined = 1,
  fabricCanvasInstanceRef: fabric.Canvas,
  dispatch: ThunkDispatch<{ editor: editorInterface }, undefined, UnknownAction> & Dispatch<UnknownAction>,
  selectedLayout: number,
  fabricElement: {
    width: number;
    text: string;
    left: number;
    top: number;
    fontSize: number;
    textAlign: any;
    fill: any;
    fontFamily: any;
    originY: any;
  },
  index: number,
  qrcode = false,
  premadeItem
) => {
  const maxHeight = qrcode
                    ? parseFloat(itemText.style.height) * scale - 150 * scale
                    : parseFloat(itemText.style.height) * scale

                    const newScale = calculateNewScale(itemText.style.width, fabricElement.width, scale || 1);
                    const textValue = premadeItem && premadeItem.value ? premadeItem.value : fabricElement.text;
                    const fontFamily = premadeItem && premadeItem?.properties?.textFontFamily ? premadeItem.properties.textFontFamily : fabricElement.fontFamily;
                    const textAlign = premadeItem && premadeItem.properties.textAlign ? premadeItem.properties.textAlign : fabricElement.textAlign;
                    const textFontColor = premadeItem && premadeItem.properties.textFontColor ? premadeItem.properties.textFontColor : fabricElement.fill;
                    const textFontSize = premadeItem && premadeItem.properties.textFontSize ? premadeItem.properties.textFontSize * scale * newScale : fabricElement.fontSize * newScale;
  const originY = premadeItem && premadeItem.properties.textVAlign ? premadeItem.properties.textVAlign : fabricElement.originY;

  const textbox = createFabricTextbox(textValue, {
    left: fabricElement.left * newScale,
    top: fabricElement.top * newScale,
    width: fabricElement.width * newScale,
    maxWidth: fabricElement.width * newScale,
    maxHeight: maxHeight,
    fontSize: textFontSize,
    textAlign: textAlign,
    fill: textFontColor,
    fontFamily: fontFamily,
    originY: originY,
    idx: index,
  });

  return configureTextBox(textbox, itemText, scale, fabricCanvasInstanceRef, dispatch, selectedLayout, index, qrcode, newScale);
};

/**
 * Creates and configures a fabric.js textbox element on a given canvas instance.
 *
 * @param {Object} itemText - The item containing text content and style information.
 * @param {number} [scale=1] - The scale factor to apply to the textbox dimensions and styles.
 * @param {fabric.Canvas} fabricCanvasInstanceRef - A reference to the fabric.js canvas instance where the textbox will
 *   be created.
 * @param {ThunkDispatch<{editor: editorInterface}, undefined, UnknownAction> & Dispatch<UnknownAction>} dispatch -
 *   Redux dispatch method for dispatching actions.
 * @param {number} selectedLayout - The currently selected layout identifier.
 * @param {number} index - The index of the textbox to be created.
 * @param {boolean} [qrcode=false] - A flag indicating if the textbox is associated with a QR code element.
 * @returns {fabric.Textbox} - The created and configured fabric.js textbox instance.
 */
export const createCanvasTextBox = async(
  itemText,
  scale: number | undefined = 1,
  fabricCanvasInstanceRef: fabric.Canvas,
  dispatch: ThunkDispatch<{ editor: editorInterface }, undefined, UnknownAction> & Dispatch<UnknownAction>,
  selectedLayout: number,
  index: number,
  qrcode = false,
  premadeItem
) => {
  const textValue = premadeItem && premadeItem.value ? premadeItem.value : itemText.elements![0].defaultContent;
  const fontFamily = premadeItem && premadeItem?.properties?.textFontFamily ? premadeItem.properties.textFontFamily : itemText.style["font-family"];
  const textAlign = premadeItem && premadeItem.properties.textAlign ? premadeItem.properties.textAlign : itemText.style["text-align"] || "center";
  const textFontColor = premadeItem && premadeItem.properties.textFontColor ? premadeItem.properties.textFontColor : itemText.style.color || "#000000";
  const textFontSize = premadeItem && premadeItem.properties.textFontSize ? premadeItem.properties.textFontSize  * scale : parseFloat(itemText.style["font-size"]) * scale;
  const originY = premadeItem && premadeItem.properties.textVAlign ? premadeItem.properties.textVAlign : "top";
  const textbox = createFabricTextbox(textValue, {
    left: parseFloat(itemText.style.left) * scale,
    top: parseFloat(itemText.style.top) * scale,
    width: parseFloat(itemText.style.width) * scale,
    maxWidth: parseFloat(itemText.style.width) * scale,
    maxHeight: qrcode
               ? parseFloat(itemText.style.height) * scale - 150 * scale
               : parseFloat(itemText.style.height) * scale,
    fontSize: textFontSize,
    fontFamily: fontFamily,
    textFont: fontFamily,
    textAlign: textAlign,
    fill: textFontColor,
    originY: originY,
    idx: index,
  });
  return configureTextBox(textbox, itemText, scale, fabricCanvasInstanceRef, dispatch, selectedLayout, index);
};
